(() => {
  if (window.LimitlessHeroMotion) { window.LimitlessHeroMotion.init(document); return; }
  const stages = new Map();
  const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const allowed = () => Boolean(observer) && !motion?.matches && !connection?.saveData && !document.hidden;
  const paint = state => {
    const running = allowed() && state.visible && !state.userPaused && state.stage.isConnected && !state.stage.closest('[hidden]');
    state.stage.dataset.llHeroMotionState = running ? 'running' : 'paused';
  };
  const refresh = () => stages.forEach(paint);
  const observer = typeof window.IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const state = stages.get(entry.target);
      if (state) { state.visible = entry.isIntersecting && entry.intersectionRatio > 0; paint(state); }
    });
  }, {threshold: [0, 0.01]}) : null;
  const init = (root = document) => {
    const nodes = [...(root.matches?.('[data-ll-hero-motion]') ? [root] : []), ...root.querySelectorAll('[data-ll-hero-motion]')];
    nodes.forEach(stage => {
      if (stages.has(stage)) return;
      const controller = new AbortController();
      const state = {stage, controller, visible: false, userPaused: stage.closest('[data-ll-shared-motion]')?.dataset.llSharedPaused === 'true'};
      stages.set(stage, state);
      paint(state);
      observer?.observe(stage);
    });
  };
  const destroy = (root = document) => {
    stages.forEach((state, stage) => {
      if (root === document || root === stage || root.contains(stage)) {
        state.controller.abort(); observer?.unobserve(stage);
        stage.dataset.llHeroMotionState = 'paused';
        stages.delete(stage);
      }
    });
  };
  document.addEventListener('visibilitychange', refresh);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
  if (motion?.addEventListener) motion.addEventListener('change', refresh); else motion?.addListener?.(refresh);
  connection?.addEventListener?.('change', refresh);
  const setPaused = (root, paused) => {
    init(root);
    stages.forEach(state => {
      if (root === document || root === state.stage || root.contains(state.stage)) { state.userPaused = Boolean(paused); paint(state); }
    });
  };
  window.LimitlessHeroMotion = {init, destroy, setPaused};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once:true}); else init();
})();

/* Brand scrolling is independent of the floating-photo and video controllers. */
(() => {
  if (window.LimitlessLogoMotion) { window.LimitlessLogoMotion.init(document); return; }
  const rows = new Map();
  const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const hover = window.matchMedia?.('(hover: hover)');
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const available = () => !motion?.matches && !connection?.saveData;
  // Start from actual band geometry; an observer callback is an optimization, not a startup prerequisite.
  const isVisible = state => {
    const rect = state.viewport.getBoundingClientRect();
    const width = window.innerWidth || document.documentElement.clientWidth;
    const height = window.innerHeight || document.documentElement.clientHeight;
    return rect.width > 0 && rect.height > 0 && rect.right > 0 && rect.left < width && rect.bottom > 0 && rect.top < height;
  };
  const paint = state => {
    const enabled = available();
    const isStatic = !enabled || state.userPaused || state.focused;
    const running = enabled && !isStatic && !state.hovered && state.visible && !document.hidden && state.row.isConnected && !state.row.closest('[hidden]');
    // perf 2026-10-11: this runs on every scroll frame; only touch the DOM when something changed so the marquee rows
    // don't invalidate style/layout (and force a reflow via scrollLeft) on each frame while the customer scrolls.
    const st = String(isStatic), rs = running ? 'running' : 'paused';
    if (state.row.dataset.llLogoStatic !== st) state.row.dataset.llLogoStatic = st;
    if (state.row.dataset.llLogoState !== rs) state.row.dataset.llLogoState = rs;
    if (!isStatic && state.viewport.scrollLeft !== 0) state.viewport.scrollLeft = 0;
  };
  const refresh = () => rows.forEach(state => { state.visible = isVisible(state); paint(state); });
  let refreshFrame = 0;
  const scheduleRefresh = () => {
    if (refreshFrame) return;
    refreshFrame = window.requestAnimationFrame(() => { refreshFrame = 0; refresh(); });
  };
  const observer = typeof window.IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    entries.forEach(entry => { const state = rows.get(entry.target); if (state) { state.visible = entry.isIntersecting && entry.intersectionRatio > 0; paint(state); } });
  }, {threshold: [0, 0.01]}) : null;
  const init = (root = document) => {
    const nodes = [...(root.matches?.('[data-ll-logo-marquee]') ? [root] : []), ...root.querySelectorAll('[data-ll-logo-marquee]')];
    nodes.forEach(row => {
      if (rows.has(row)) return;
      const viewport = row.querySelector('[data-ll-logo-viewport]');
      const track = row.querySelector('[data-ll-logo-track]'), list = row.querySelector('[data-ll-logo-list]');
      if (!viewport || !track || !list) return;
      const clone = list.cloneNode(true);
      clone.removeAttribute('data-ll-logo-list'); clone.setAttribute('data-ll-logo-duplicate', ''); clone.setAttribute('aria-hidden', 'true'); clone.setAttribute('inert', '');
      clone.querySelectorAll('[id], [data-block-id], [data-shopify-editor-block]').forEach(node => { node.removeAttribute('id'); node.removeAttribute('data-block-id'); node.removeAttribute('data-shopify-editor-block'); });
      clone.querySelectorAll('a').forEach(link => { link.removeAttribute('href'); link.removeAttribute('aria-label'); link.setAttribute('tabindex', '-1'); });
      track.append(clone);
      const controller = new AbortController(), options = {signal:controller.signal};
      const state = {row, viewport, track, list, clone, controller, visible:false, userPaused:row.closest('[data-ll-shared-motion]')?.dataset.llSharedPaused === 'true', focused:false, hovered:false};
      rows.set(row, state); row.dataset.llLogoEnhanced = 'true';
      const measure = () => { const width = list.getBoundingClientRect().width; if (width > 0) track.style.setProperty('--ll-logo-duration', `${Math.max(24, width / 76)}s`); };
      state.resize = typeof window.ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
      state.resize?.observe(list); measure();
      viewport.addEventListener('pointerenter', () => { state.hovered = Boolean(hover?.matches); paint(state); }, options);
      viewport.addEventListener('pointerleave', () => { state.hovered = false; paint(state); }, options);
      viewport.addEventListener('focusin', event => {
        state.focused = true; paint(state);
        const target = event.target.closest('a');
        if (target) { const rect = target.getBoundingClientRect(), bounds = viewport.getBoundingClientRect(); if (rect.left < bounds.left) viewport.scrollLeft += rect.left - bounds.left - 12; else if (rect.right > bounds.right) viewport.scrollLeft += rect.right - bounds.right + 12; }
      }, options);
      viewport.addEventListener('focusout', event => { if (!viewport.contains(event.relatedTarget)) { state.focused = false; paint(state); } }, options);
      state.visible = isVisible(state); paint(state); observer?.observe(row); scheduleRefresh();
    });
  };
  const destroy = (root = document) => {
    rows.forEach((state, row) => {
      if (root === document || root === row || root.contains(row)) {
        state.controller.abort(); state.resize?.disconnect(); observer?.unobserve(row); state.clone.remove();
        row.dataset.llLogoState = 'paused'; row.dataset.llLogoStatic = 'true'; delete row.dataset.llLogoEnhanced;
        rows.delete(row);
      }
    });
  };
  document.addEventListener('visibilitychange', refresh);
  // iOS page restoration, late styles, and preview resizing can happen without a new observer delivery.
  window.addEventListener('pageshow', refresh);
  window.addEventListener('load', refresh);
  window.addEventListener('resize', scheduleRefresh);
  if (!observer) window.addEventListener('scroll', scheduleRefresh, {passive:true});
  window.visualViewport?.addEventListener('resize', scheduleRefresh);
  window.visualViewport?.addEventListener('scroll', scheduleRefresh);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
  if (motion?.addEventListener) motion.addEventListener('change', refresh); else motion?.addListener?.(refresh);
  connection?.addEventListener?.('change', refresh);
  const setPaused = (root, paused) => {
    init(root);
    rows.forEach(state => {
      if (root === document || root === state.row || root.contains(state.row)) { state.userPaused = Boolean(paused); paint(state); }
    });
  };
  window.LimitlessLogoMotion = {init, destroy, setPaused};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once:true}); else init();
})();
